/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import * as i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { de } from "./de/de";
import { en } from "./en/en";

type SupportedLanguage = "de" | "en";

// translations
export const resources = {
  de: {
    translation: de,
  },
  en: {
    translation: en,
  },
};

// Sprache aus verschiedenen Quellen ermitteln
const getInitialLanguage = (): SupportedLanguage => {
  // 1. URL-Parameter prüfen
  const urlParams = new URLSearchParams(window.location.search);
  const urlLang = urlParams.get("lang") as SupportedLanguage;
  if (urlLang && ["de", "en"].includes(urlLang)) {
    return urlLang;
  }

  // 2. Cookie prüfen
  const cookies = document.cookie.split(";");
  const cookie = cookies.find((c) => c.trim().startsWith("preferredLanguage="));
  const cookieLang = cookie
    ? (cookie.split("=")[1] as SupportedLanguage)
    : null;
  if (cookieLang && ["de", "en"].includes(cookieLang)) {
    return cookieLang;
  }

  // 3. localStorage prüfen
  const storageLang = localStorage.getItem(
    "preferredLanguage"
  ) as SupportedLanguage;
  if (storageLang && ["de", "en"].includes(storageLang)) {
    return storageLang;
  }

  // 4. Browser-Sprache prüfen
  const browserLang = navigator.language.split("-")[0] as SupportedLanguage;
  if (["de", "en"].includes(browserLang)) {
    return browserLang;
  }

  // 5. Standard-Sprache
  return "de";
};

const initialLanguage = getInitialLanguage();
console.log(`[i18n] Initializing with language: ${initialLanguage}`);

i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage,
  fallbackLng: "de",
  defaultNS: "translation",
  debug: false,
  interpolation: {
    escapeValue: false, // react already safes from xss
  },
  react: {
    transSupportBasicHtmlNodes: true, // allow <br/> and simple html elements in translations
    transKeepBasicHtmlNodesFor: ["br", "strong", "i", "ul", "li", "wbr"], // do not transform <br/> to <br></br>
  },
});

export default i18n;
